import type { Block, NewsValue } from '@kioskforge/shared';
import {
  EMPTY_BINDING,
  attributionText,
  formatLocalTime,
  loadValue,
  type WidgetDataBinding
} from './widgetData';
import { AttributionLine, StaleNotice, WidgetStateNotice } from './widgetState';

export type NewsWidget = Extract<Block, { type: 'news' }>;

/**
 * X3.4 — the RSS/Atom news widget's React half.
 *
 * Link attribution only (§5.4): each item renders its title, its source label and
 * a link to the original — never the feed's body text, and never
 * `dangerouslySetInnerHTML`. The `url` is used as given.
 *
 * `mode: 'ticker'` is a horizontal strip whose CSS animation duration comes from
 * the block's `speed_s`; `mode: 'list'` is a static list. The animation is
 * presentational, so it degrades to the first item under reduced motion.
 */
export function NewsWidgetView({
  block,
  binding = EMPTY_BINDING
}: {
  block: NewsWidget;
  binding?: WidgetDataBinding;
}) {
  const state = loadValue<NewsValue>(binding, 'news', block.props.source.data_key);
  const timezone = binding.timezone;

  if (state.status !== 'ready' && state.status !== 'stale') {
    return <WidgetStateNotice status={state.status} reason={state.status === 'error' ? state.reason : undefined} />;
  }

  const value = state.value;
  const props = block.props;
  // Clamp to what the feed actually carries; never invent headlines.
  const items = value.items.slice(0, props.max_items);
  const attribution = attributionText(value, binding, 'news');

  return (
    <div className={`player-news player-news-${props.mode}`} data-news-mode={props.mode}>
      {state.status === 'stale' ? <StaleNotice lastGood={state.lastGood} timezone={timezone} /> : null}

      <ul className="player-news-items" style={props.mode === 'ticker' ? { animationDuration: `${props.speed_s}s` } : undefined}>
        {items.map((item) => (
          <li key={item.url} className="player-news-item">
            <a className="player-news-title" href={item.url} target="_blank" rel="noreferrer">
              {item.title}
            </a>
            <span className="player-news-meta mono">
              {props.show_source ? <span className="player-news-source">{item.source}</span> : null}
              {props.show_time && item.published_at ? <span>{formatLocalTime(item.published_at, timezone)}</span> : null}
            </span>
          </li>
        ))}
        {items.length === 0 ? <li className="player-widget-state-label">No headlines</li> : null}
      </ul>

      {attribution ? <AttributionLine text={attribution} url={value.attribution?.url} /> : null}
    </div>
  );
}
